// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/Experiences.tsx
// 🔧 PURPOSE: "Esperienze e noleggi": Delivery, Escursioni, Massaggi,
//             Noleggio auto, Noleggio bici, Shop online. Ognuna apre la sua
//             pagina (ServiceDialogsHost, openService); lo shop
//             apre il negozio online.
//
//             Due forme:
//             - ExperiencesGrid: la griglia 2×3 sotto i servizi, nel
//               soggiorno (con la didascalia "ESPERIENZE E NOLEGGI");
//             - ExperiencesSheet: le stesse sei in un foglio dal basso,
//               aperto dalla riga "Esperienze e noleggi" delle viste ospite
//               e prima dell'arrivo (lì la pagina resta corta).
//             "Escursioni" e non "in barca": la pagina ha anche il tour
//             UNESCO e l'escursione su misura.
//
//             Dal foglio, la pagina si apre col foglio segnato nella voce
//             della Home (`returnTo`, ?sheet=experiences): indietro si torna
//             alla Home col foglio ancora aperto (HomeSheet,
//             useHomeSheetState). Il foglio resta su finché la pagina arriva.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bike, Car, Heart, Sailboat, ShoppingBag, UtensilsCrossed, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useServiceDialogs, type ServiceKind } from './services/ServiceDialogsHost';
import HomeSheet from './HomeSheet';
import { CardShell, Eyebrow, FOCUS_RING, IconCircle, INK_TEXT } from './ui';

/** Il negozio online della struttura. */
export const SHOP_URL = 'https://store.nonnavittoriaapartments.it';

type ExperienceKey = 'delivery' | 'excursions' | 'massage' | 'rentCar' | 'rentBike' | 'shop';

const EXPERIENCES: { key: ExperienceKey; icon: LucideIcon; service?: ServiceKind }[] = [
  { key: 'delivery', icon: UtensilsCrossed, service: 'delivery' },
  { key: 'excursions', icon: Sailboat, service: 'excursions' },
  { key: 'massage', icon: Heart, service: 'massage' },
  { key: 'rentCar', icon: Car, service: 'rentCar' },
  { key: 'rentBike', icon: Bike, service: 'rentBike' },
  { key: 'shop', icon: ShoppingBag },
];

interface TilesProps {
  /** Lo shop (fuori dall'app): chiamata al tocco, il foglio si chiude. */
  onPick?: () => void;
  /** Dal foglio: la voce della Home col foglio aperto, dove torna il "‹" della pagina. */
  returnTo?: string;
}

const ExperienceTiles: React.FC<TilesProps> = ({ onPick, returnTo }) => {
  const { t } = useTranslation();
  const { openService } = useServiceDialogs();
  return (
    <ul className="grid grid-cols-2 gap-2">
      {EXPERIENCES.map(({ key, icon: Icon, service }) => {
        const content = (
          <>
            <IconCircle size={34}>
              <Icon />
            </IconCircle>
            <span className={cn('text-[14px] font-bold leading-tight', INK_TEXT)}>{t(`home.experiences.${key}`)}</span>
          </>
        );
        const classes = cn(
          'flex min-h-[56px] w-full items-center gap-2.5 p-2.5 text-left transition-colors hover:bg-nva-cream/50 dark:hover:bg-stone-800/70',
          FOCUS_RING,
        );
        return (
          <li key={key}>
            <CardShell className="h-full overflow-hidden rounded-[14px]">
              {service ? (
                <button
                  type="button"
                  className={classes}
                  onClick={() => openService(service, returnTo ? { backTo: returnTo } : undefined)}
                >
                  {content}
                </button>
              ) : (
                <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className={classes} onClick={onPick}>
                  {content}
                </a>
              )}
            </CardShell>
          </li>
        );
      })}
    </ul>
  );
};

/** La griglia del soggiorno, con la sua didascalia. */
export const ExperiencesGrid: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <Eyebrow tone="muted" as="p" className="pt-1 text-[12px] tracking-[0.14em]">
        {t('home.experiences.eyebrow')}
      </Eyebrow>
      <ExperienceTiles />
    </div>
  );
};

/** Il foglio dal basso delle viste ospite e prima dell'arrivo. */
export const ExperiencesSheet: React.FC<{ open: boolean; onClose: () => void; returnTo?: string }> = ({ open, onClose, returnTo }) => {
  const { t } = useTranslation();
  return (
    <HomeSheet
      open={open}
      onClose={onClose}
      titleId="home-experiences-sheet-title"
      eyebrow={t('home.services.guestEyebrow')}
      title={t('home.services.experiencesTitle')}
    >
      <ExperienceTiles onPick={onClose} returnTo={returnTo} />
    </HomeSheet>
  );
};

export default ExperiencesGrid;
